import { useSensors, useSensor, TouchSensor, MouseSensor, KeyboardSensor, DndContext, closestCenter, defaultDropAnimationSideEffects, defaultDropAnimation } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useRef, useState, useMemo, Fragment } from 'react';
import { ChevronIcon } from '@ui/kit/icons';
import { sequenceTileInput } from '@utils/component-framework';
import { NoSSR } from '@utils/react';
import { useComponentRootRef } from '../../../../../context.js';
import { useOpgavekompleksetEntry } from '../../../../../state/hook.entry.js';
import { useUpdateTaskEntryMutation } from '../../../../../state/mutations/update-task-entry.js';
import { TaskAutoScroller } from '../../../../../utils/dnd-kit.auto-scroll.js';
import { makeRestrictToRef } from '../../../../../utils/dnd-kit.restrict-to-container.js';
import { OpgavekompleksetTaskDragOverlay } from '../../../../atom.drag-overlay/_drag-overlay.js';
import { SequenceInput } from '../input.content.js';
import { DraggableInput } from '../input.draggable.js';
import { orderInputs } from '../util.order-inputs.js';
import { updateInputsOnDrag } from '../util.update-inputs-on-drag-over.js';
import { wrapper, container, chevron, dragOverlay } from './container.input.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function VerticalInputContainer(props) {
  const componentRef = useComponentRootRef();
  const ref = useRef(null);
  const [draggedInputId, setDraggedInputId] = useState();
  const update = useUpdateTaskEntryMutation();
  const initialInputEntries = useOpgavekompleksetEntry({
    select: entry => Object.entries(entry?.data.tasks[props.taskEntryKey]?.details ?? {}).map(([key, value]) => {
      const typedValue = sequenceTileInput.entry.asTypeSafe(value);
      if (typedValue) {
        return {
          nodeId: key,
          ...typedValue
        };
      }
    }).filter(it => !!it)
  });
  const [inputEntries, setInputEntries] = useState(initialInputEntries);
  const inputs = useMemo(() => orderInputs(props.task.content, inputEntries), [inputEntries, props.task.content]);
  const draggableInputs = inputs.filter(it => !it.options.fixed);
  const sensors = useSensors(useSensor(TouchSensor, {
    activationConstraint: {
      distance: 6
    }
  }), useSensor(MouseSensor, {
    activationConstraint: {
      distance: 6
    }
  }), useSensor(KeyboardSensor, {
    scrollBehavior: "instant"
  }));
  const handleDragStart = event => {
    setDraggedInputId(event.active.id.toString());
  };
  const handleDragOver = event => {
    updateInputsOnDrag({
      taskEntryKey: props.taskEntryKey,
      inputs,
      update,
      updateUI: setInputEntries
    }, event);
  };
  const handleDragEnd = () => {
    setDraggedInputId(undefined);
  };
  const draggedInput = draggableInputs.find(it => it.id === draggedInputId);
  const restrictToComponent = useMemo(() => makeRestrictToRef(componentRef), [componentRef]);
  return /*#__PURE__*/jsx("div", {
    className: wrapper,
    children: /*#__PURE__*/jsx(NoSSR, {
      children: /*#__PURE__*/jsxs(DndContext, {
        sensors: sensors,
        collisionDetection: closestCenter,
        onDragStart: handleDragStart,
        onDragOver: handleDragOver,
        onDragEnd: handleDragEnd,
        onDragCancel: handleDragEnd,
        modifiers: [restrictToVerticalAxis, restrictToComponent],
        autoScroll: false,
        children: [/*#__PURE__*/jsx(SortableContext, {
          items: draggableInputs,
          strategy: verticalListSortingStrategy,
          children: /*#__PURE__*/jsxs("div", {
            ref: ref,
            className: container,
            "data-is-drag-active": !!draggedInputId,
            children: [inputs.map(input => /*#__PURE__*/jsxs(Fragment, {
              children: [input.options.fixed ? /*#__PURE__*/jsx(SequenceInput, {
                node: input,
                taskEntryKey: props.taskEntryKey
              }) : /*#__PURE__*/jsx(DraggableInput, {
                isDraggingItem: draggedInputId === input.id,
                node: input,
                taskEntryKey: props.taskEntryKey
              }), /*#__PURE__*/jsx(ChevronIcon, {
                className: chevron,
                height: 8,
                strokeWidth: 3
              })]
            }, input.id)), /*#__PURE__*/jsx(OpgavekompleksetTaskDragOverlay, {
              className: dragOverlay,
              task: props.task,
              dropAnimation: {
                ...defaultDropAnimation,
                sideEffects: defaultDropAnimationSideEffects({
                  styles: {
                    active: {
                      opacity: "0.5"
                    }
                  }
                })
              },
              children: draggedInput && /*#__PURE__*/jsx(SequenceInput, {
                isDragOverlay: true,
                isDraggable: true,
                node: draggedInput,
                taskEntryKey: props.taskEntryKey
              }, draggedInput.id)
            })]
          })
        }), /*#__PURE__*/jsx(TaskAutoScroller, {
          taskRef: ref
        })]
      })
    })
  });
}

export { VerticalInputContainer };
//# sourceMappingURL=container.input.js.map
